/**
 * SaveReportButton — *Uložiť report* on a finished research run (PRD-0076).
 *
 * Before the first save: one button that saves the report and only the evidence it
 * cites as an investigation. After it: *Zobraziť vyšetrovanie* opens that investigation,
 * and a small refresh button saves again — the same investigation is brought up to the
 * report, nothing the analyst added is removed.
 *
 * Pure: the connected `SaveResearchReportAction` (features/hypotheses) supplies the
 * state and the handlers.
 */

import { ArrowRight, FileCheck2, Loader2, RefreshCw } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { Button } from '@/components/ui/button'

export interface SaveReportButtonProps {
  /** The investigation an earlier save created; null before the first save. */
  savedInvestigationId: string | null
  saving: boolean
  onSave: () => void
  onOpen: (investigationId: string) => void
}

export function SaveReportButton({
  savedInvestigationId,
  saving,
  onSave,
  onOpen,
}: SaveReportButtonProps) {
  const { t } = useTranslation()
  const busy = saving ? <Loader2 className="size-3.5 animate-spin" /> : null

  if (!savedInvestigationId) {
    return (
      <Button
        type="button"
        size="sm"
        className="gap-1.5 max-md:h-9"
        onClick={onSave}
        disabled={saving}
        aria-busy={saving}
      >
        {busy ?? <FileCheck2 className="size-3.5" />}
        {saving ? t('research.saveReport.saving') : t('research.saveReport.save')}
      </Button>
    )
  }

  return (
    <div className="inline-flex items-center gap-1">
      <Button
        type="button"
        size="sm"
        variant="outline"
        className="group gap-1.5 max-md:h-9"
        onClick={() => onOpen(savedInvestigationId)}
      >
        {t('research.saveReport.open')}
        <ArrowRight className="size-3.5 transition-transform group-hover:translate-x-0.5" />
      </Button>
      <Button
        type="button"
        size="icon-sm"
        variant="ghost"
        className="max-md:size-9"
        onClick={onSave}
        disabled={saving}
        aria-busy={saving}
        aria-label={t('research.saveReport.resave')}
        title={t('research.saveReport.resave')}
      >
        {busy ?? <RefreshCw className="size-3.5" />}
      </Button>
    </div>
  )
}
